import React, { useEffect, useRef, useState } from "react";
import { NewsItem } from "@/models/server/NewsItem";
import { Carousel, CarouselNavigation, CarouselPagination } from "@renta-apps/athenaeum-react-components";
import styles from "./NewsCarousel.module.scss";
import NewsCarouselItem from "@/components/NewsCarousel/NewsCarouselItem";

interface INewsCarouselProps {
    newsItems: NewsItem[];
    className?: string;
}

const NewsCarousel: React.FC<INewsCarouselProps> = ({ newsItems = [], className }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const intervalId = useRef<NodeJS.Timeout>();
    const carouselRef = useRef<Carousel>(null);
    const carouselInteractions = [
        "athenaeum-carousel-next",
        "athenaeum-carousel-previous",
        "swiper-pagination-bullet"
    ];

    // Auto-scroll effect
    useEffect(() => {
        if (newsItems.length > 1 &&
            // when the user is not interacting with the carousel (swipe)
            // allow auto scroll
            (carouselRef.current?.state.swiper?.touches?.diff === undefined || carouselRef.current?.state.swiper?.touches?.diff === 0)
        ) {
            intervalId.current = setInterval(() => {
                setCurrentIndex(prevIndex => {
                    const nextIndex = (prevIndex + 1) % newsItems.length;
                    carouselRef.current?.slideToAsync(nextIndex);
                    return nextIndex;
                });
            }, 5000);
        }
        return () => {
            if (intervalId.current) {
                clearInterval(intervalId.current);
            }
        };
    }, [newsItems.length, carouselRef.current?.state.swiper?.touches?.diff]);

    // Sync state when user navigates manually
    const handleSlideChange = async (index: number): Promise<void> => {
        setCurrentIndex(index);
    };

    const handleOnClick = async (event: React.MouseEvent): Promise<void> => {
        var target = event.target as HTMLElement;

        if (!target) {
            return;
        }

        // when the user clicks the arrow or pagination bullet to change the news, autoscroll will be turned off. 
        // This way user has time to read the news
        if (carouselInteractions.some(className => target?.classList.contains(className)) && intervalId.current) {
            clearInterval(intervalId.current);
            intervalId.current = undefined;
        }
    };

    if (newsItems.length === 0) {
        return null;
    }


    return (
        <div id="NewsCarousel">
            <Carousel
                ref={carouselRef}
                loop={true}
                className={[className, styles.newsCarousel].filter(Boolean).join(" ")}
                navigation={CarouselNavigation.Inside}
                pagination={CarouselPagination.BottomInside}
                slidesPerView={1}
                initialSlideIndex={currentIndex}
                onSlideChange={handleSlideChange}
                onClick={handleOnClick}
            >
                {newsItems.map((newsItem: NewsItem, index: number) =>
                    <div key={index}>
                        <NewsCarouselItem
                            newsItem={newsItem}
                            index={index}
                        />
                    </div>
                )}
            </Carousel>
        </div>
    );
};

export default NewsCarousel;